import React, { useEffect, useMemo, useState } from 'react';
import { Compare, ComparisonListStatus } from '@navi-toolbox/ui-components';
import { useTranslation } from 'react-i18next';
import { SnackbarProvider, Tooltip } from 'fronton-react';
import { useBreakpoints } from 'common/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { getCompareCategory, getProductId } from 'features/product-info';
import {
  productsCompareListsActions,
  getIsInComparison,
  getCompareListsError,
} from 'features/products-compare';

export const PdpComparison: React.FC = () => {
  const dispatch = useDispatch();
  const breakpoints = useBreakpoints();
  const isDesktop = useMemo(() => breakpoints?.largeDesktop || breakpoints?.desktop, [breakpoints]);
  const { t } = useTranslation();

  const compareCategory = useSelector(getCompareCategory);
  const groupId = compareCategory?.familyId || null;

  const productId = useSelector(getProductId) || '';
  const isActiveFromStore = useSelector(getIsInComparison(groupId, productId));
  const error = useSelector(getCompareListsError);

  const [optimisticIsActive, setOptimisticIsActive] = useState(isActiveFromStore);
  const [showStatus, setShowStatus] = useState(false);
  const [statusType, setStatusType] = useState<'added' | 'removed' | 'error'>('added');

  useEffect(() => {
    setOptimisticIsActive(isActiveFromStore);
  }, [isActiveFromStore]);

  useEffect(() => {
    if (error) {
      setStatusType('error');
      setShowStatus(true);
    }
  }, [error]);

  const handleAddToCompare = () => {
    setOptimisticIsActive(true);
    dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'add' }));
    setStatusType('added');
    setShowStatus(true);
  };

  const handleRemoveFromCompare = () => {
    setOptimisticIsActive(false);
    dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'delete' }));
    setStatusType('removed');
    setShowStatus(true);
  };

  const onClick = optimisticIsActive ? handleRemoveFromCompare : handleAddToCompare;

  const redirectTo = statusType === 'added' ? '/compare/' : undefined;

  const handleErrorClick = () => {
    setShowStatus(false);
    onClick();
  };

  const handleMoveToComparePage = () => {
    setShowStatus(false);
  };

  const tipText = optimisticIsActive
    ? t('ui.buttons.compare-tooltip-remove')
    : t('ui.buttons.compare-tooltip-add');

  const buttonText = optimisticIsActive ? t('ui.buttons.compareAdded') : t('ui.buttons.compareAdd');

  const statusConfig = {
    added: {
      text: t('ui.snackbar.compareAddedText'),
      buttonText: t('ui.snackbar.moveTo'),
      action: handleMoveToComparePage,
    },
    removed: {
      text: t('ui.snackbar.compareRemovedText'),
      buttonText: t('ui.snackbar.dismiss'),
      action: onClick,
    },
    error: {
      text: t('ui.snackbar.compareErrorText'),
      buttonText: t('ui.snackbar.tryAgain'),
      action: handleErrorClick,
    },
  };

  const currentStatusConfig = statusConfig[statusType];

  const button = <Compare onClick={onClick} buttonText={buttonText} active={optimisticIsActive} />;

  return (
    <>
      {showStatus && (
        <SnackbarProvider>
          <ComparisonListStatus
            id={productId}
            hrefForButton={redirectTo}
            mode={statusType}
            text={currentStatusConfig.text}
            buttonText={currentStatusConfig.buttonText}
            handleClickButton={currentStatusConfig.action}
          />
        </SnackbarProvider>
      )}
      {isDesktop ? (
        <Tooltip placement="top" title={tipText}>
          {button}
        </Tooltip>
      ) : (
        button
      )}
    </>
  );
};
